import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchBar from 'components/SearchBar';
import Breadcrumbs from 'components/Breadcrumbs';
import UserSDSManager from 'components/UserSDSManager';
import { useAuth } from 'context/AuthContext';
import { useTranslation } from 'react-i18next';
import { listSDS, uploadSDS, deleteSDS, downloadSDS } from 'utils/methods/safetynow';
// import { useLocationStore } from '../../../context/LocationContext'; // Future use

const SafetyNow = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token, openLoginModal } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  // Future: These context variables will be used for location profile management
  // const { selectedLocationProfile } = useLocationStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [userSdsFiles, setUserSdsFiles] = useState([]);
  const [showSdsManager, setShowSdsManager] = useState(false);

  const recentSearches = [
    'H2O', 'NaCl', 'C2H5OH', 'methanol', 'ethanol', 'chlorine', 
    'sodium methoxide', 'sulfuric acid', 'carbon disulphide', 
    'hydrogen peroxide', 'bromine', 'trifluoroacetyl chloride', 'ammonia'
  ];

  const performSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/tools/safety-now/search?query=${encodeURIComponent(searchQuery.trim())}`);
    }
  };
  const quickSearch = (term) => {
    setSearchQuery(term);
    navigate(`/tools/safety-now/search?query=${encodeURIComponent(term)}`);
  };

  // Load user's SDS files when component mounts and user is authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      loadUserSdsFiles();
    }
  }, [isAuthenticated, token]);

  const loadUserSdsFiles = async () => {
    try {
      const response = await listSDS();
      setUserSdsFiles(response.data.sds_documents || []);
    } catch (error) {
      console.error('Error loading SDS files:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if user is authenticated
    if (!isAuthenticated) {
      setUploadError('Please log in to upload SDS files.');
      return;
    }
    
    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', `SDS uploaded from Safety Now - ${file.name}`);
      
      await uploadSDS(formData);
      
      setUploadSuccess(`File "${file.name}" uploaded successfully!`);
      
      // Reload the user's SDS files
      await loadUserSdsFiles();
      
      // Clear file input
      e.target.value = '';
      
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.response?.status === 401) {
        setUploadError('Authentication required. Please log in again.');
      } else if (error.response?.data?.detail) {
        setUploadError(error.response.data.detail);
      } else {
        setUploadError('Failed to upload file. Please try again.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const downloadSdsFile = async (sdsId, filename) => {
    try {
      const response = await downloadSDS(sdsId);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const deleteSdsFile = async (sdsId) => {
    if (!window.confirm('Are you sure you want to delete this SDS file?')) return;
    
    try {
      await deleteSDS(sdsId);
      
      // Reload the user's SDS files
      await loadUserSdsFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white via-blue-50 to-blue-100">
      {/* Hero Section with Enhanced Design */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-center text-gray-800 mb-4">
            {t('safetyNow.title')}
          </h1>
          <p className="text-xl text-center text-gray-600 mb-8">
            {t('safetyNow.subtitle')}
          </p>
        </div>
      </div>

      {/* Main Content Area with Glass Morphism */}
      <div className="container mx-auto px-4 pb-12">
        <div className="max-w-4xl mx-auto">
          <Breadcrumbs />
          
          {/* Tabs Navigation - Tailwind Style */}
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab(0)}
                  className={`${
                    activeTab === 0
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                >
                  {t('safetyNow.searchDatabase')}
                </button>
                <button
                  onClick={() => setActiveTab(1)}
                  className={`${
                    activeTab === 1
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                >
                  {t('safetyNow.mySdsFiles')}
                </button>
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 0 && (
            <>

          {/* Search Section */}
          <div className="backdrop-blur-md bg-white/40 rounded-2xl p-8 shadow-xl border border-white/50">
            <div className="space-y-6">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">{t('safetyNow.searchChemicalDatabase')}</h2>
                  <p className="text-gray-600">{t('safetyNow.searchChemicalDatabaseDesc')}</p>
                </div>
              </div>

              {/* Enhanced Search Bar */}

              {!isAuthenticated ? (
                /* Authentication Required Message */
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('safetyNow.authRequired')}</h3>
                  <p className="text-gray-600 mb-4">{t('safetyNow.authRequiredDesc')}</p>
                  <div className="space-x-4">
                    <button onClick={() => openLoginModal()} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      {t('safetyNow.logIn')}
                    </button>
                    <button onClick={() => navigate('/register')} className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                      {t('safetyNow.signUp')}
                    </button>
                  </div>
                </div>
              ) : (
                <div>

                  <div className="relative">
                    <SearchBar 
                      searchQuery={searchQuery} 
                      onSearchQueryChange={setSearchQuery}
                      performSearch={performSearch} 
                      placeholder="Example: H2O, Sodium Chloride, etc."
                      className="search-container" 
                    />

                    {/* Recent Searches */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {recentSearches.map((term) => (
                        <button 
                          key={term} 
                          onClick={() => quickSearch(term)}
                          className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm hover:bg-blue-100 transition-colors duration-200"
                        >
                          {term}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Search Tips */}
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold text-blue-800 mb-2">{t('safetyNow.searchTips')}</h3>
                    <ul className="text-sm text-blue-700 space-y-1 list-disc pl-8">
                      <li>{t('safetyNow.searchTip1')}</li>
                      <li>{t('safetyNow.searchTip2')}</li>
                    </ul>
                  </div>

                </div>
              )}

            </div>
          </div>
            </>
          )}

          {/* Tab 2: My SDS Files */}
          {activeTab === 1 && (
            !isAuthenticated ? (
                /* Authentication Required Message */
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('safetyNow.authRequired')}</h3>
                  <p className="text-gray-600 mb-4">{t('safetyNow.authRequiredDesc')}</p>
                  <div className="space-x-4">
                    <button onClick={() => openLoginModal()} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      {t('safetyNow.logIn')}
                    </button>
                    <button onClick={() => navigate('/register')} className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                      {t('safetyNow.signUp')}
                    </button>
                  </div>
                </div>
              ) : (
              <UserSDSManager />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default SafetyNow;
